import React, { useLayoutEffect, useRef, useState } from 'react';
import { SupplierView } from './discoveryModel';
import {
  TRUST_BAR,
  buildMatrix,
  estLabelWidth,
  placeMatrixLabels,
  LabelBox,
  MatrixPoint,
} from './discoveryInsights';

/**
 * Supplier Trust Matrix (v2.1 mock's trust-vs-cost plot, renamed by the
 * owner): all suppliers with a stored price AND trust score plotted, split
 * into BEST VALUE / PREMIUM / RISKY SAVINGS / AVOID at the buyer's target
 * price and the trust bar. Without a target price there is no vertical
 * split — only the trust bar. Suppliers missing either number are listed
 * as not plotted instead of being placed somewhere invented.
 *
 * Rendering: SVG scatter with collision-free name labels (layout in
 * discoveryInsights.placeMatrixLabels); price / lead / trust live in the
 * hover tooltip, and every number also appears in the pick cards and the
 * other-suppliers accordion, so nothing is tooltip-gated.
 */
interface SupplierTrustMatrixProps {
  suppliers: SupplierView[];
  shortlistedNames: string[];
  targetPrice?: number;
}

const MARGIN = { top: 10, right: 14, bottom: 36, left: 46 };
const PLOT_H = 250;
const HALO: React.CSSProperties = { paintOrder: 'stroke', strokeLinejoin: 'round' };

const useMeasuredWidth = (): [React.RefObject<HTMLDivElement | null>, number] => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
};

const QuadrantLabel: React.FC<{ x: number; y: number; anchor: 'start' | 'end'; tone: string; children: string }> = ({
  x,
  y,
  anchor,
  tone,
  children,
}) => (
  <text x={x} y={y} textAnchor={anchor} className={`${tone} stroke-surface text-[9.5px] font-bold tracking-[0.08em]`} strokeWidth={3} style={HALO}>
    {children}
  </text>
);

export const SupplierTrustMatrix: React.FC<SupplierTrustMatrixProps> = ({
  suppliers,
  shortlistedNames,
  targetPrice,
}) => {
  const [plotRef, width] = useMeasuredWidth();
  const [hovered, setHovered] = useState<MatrixPoint | null>(null);
  const matrix = buildMatrix(suppliers, shortlistedNames, targetPrice);
  if (!matrix) return null;
  const { points, unplotted, targetX, barY, priceTicks, trustTicks, currencySymbol } = matrix;

  const splitCaption =
    targetX != null && targetPrice != null
      ? `split at your target price (${currencySymbol}${targetPrice.toLocaleString()}) and trust bar (${TRUST_BAR})`
      : `split at your trust bar (${TRUST_BAR})`;

  const plotW = Math.max(0, width - MARGIN.left - MARGIN.right);
  const px = (pct: number) => (pct / 100) * plotW;
  const py = (pct: number) => (pct / 100) * PLOT_H;
  const targetPx = targetX != null ? px(targetX) : undefined;
  const barPx = py(barY);

  // Line captions and quadrant tags claim their space first so supplier
  // names never sit on top of them.
  const reserved: LabelBox[] = [];
  if (targetPx != null && targetPrice != null) {
    const caption = `target ${currencySymbol}${targetPrice.toLocaleString()}`;
    reserved.push({ x: targetPx + 4, y: 2, w: estLabelWidth(caption), h: 13 });
    reserved.push(
      { x: 6, y: 4, w: 70, h: 13 },
      { x: plotW - 66, y: 4, w: 60, h: 13 },
      { x: 6, y: PLOT_H - 17, w: 96, h: 13 },
      { x: plotW - 46, y: PLOT_H - 17, w: 40, h: 13 },
    );
  }
  reserved.push({ x: plotW - 74, y: barPx - 16, w: 70, h: 13 });

  const pxPoints = points.map((p) => ({ ...p, x: px(p.x), y: py(p.y) }));
  const labels = plotW > 0 ? placeMatrixLabels(pxPoints, plotW, PLOT_H, reserved) : new Map();

  // `hovered` comes from pxPoints, so its x/y are already pixels.
  const tooltipLeft = hovered ? MARGIN.left + hovered.x : 0;
  const tooltipFlip = hovered != null && hovered.x > plotW - 170;

  return (
    <div className="shrink-0 rounded-card border border-line bg-surface px-[16px] py-[14px]">
      <div className="mb-[10px] flex flex-wrap items-baseline gap-s4">
        <span className="text-card-header text-ink">Supplier Trust Matrix</span>
        <span className="text-meta text-ink-muted">
          {points.length === suppliers.length ? `All ${suppliers.length}` : `${points.length} of ${suppliers.length}`}{' '}
          suppliers · {splitCaption}
        </span>
        <span className="ml-auto inline-flex shrink-0 items-center gap-s4">
          <span className="inline-flex items-center gap-[5px] text-[10.5px] text-ink-secondary">
            <span className="h-[9px] w-[9px] rounded-round bg-action" />
            Shortlisted
          </span>
          <span className="inline-flex items-center gap-[5px] text-[10.5px] text-ink-secondary">
            <span className="h-[9px] w-[9px] rounded-round border-[1.5px] border-ink-muted bg-surface" />
            Not shortlisted
          </span>
        </span>
      </div>

      <div ref={plotRef} className="relative min-w-0">
        {width > 0 && (
          <svg width={width} height={PLOT_H + MARGIN.top + MARGIN.bottom} className="block font-sans">
            <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
              {/* Quadrant washes + frame */}
              {targetPx != null ? (
                <>
                  <rect width={targetPx} height={barPx} className="fill-success-ink" fillOpacity={0.045} />
                  <rect x={targetPx} width={plotW - targetPx} height={barPx} className="fill-action" fillOpacity={0.04} />
                  <rect y={barPx} width={targetPx} height={PLOT_H - barPx} className="fill-warn-ink" fillOpacity={0.05} />
                  <rect x={targetPx} y={barPx} width={plotW - targetPx} height={PLOT_H - barPx} className="fill-danger-ink" fillOpacity={0.04} />
                </>
              ) : (
                <rect width={plotW} height={PLOT_H} className="fill-surface-hover" />
              )}

              {/* Hairline grid at clean price / trust steps */}
              {priceTicks.map((t) => (
                <line key={`gx${t.pct}`} x1={px(t.pct)} x2={px(t.pct)} y2={PLOT_H} className="stroke-line-divider" />
              ))}
              {trustTicks.map((t) => (
                <line key={`gy${t.pct}`} y1={PLOT_H - py(t.pct)} y2={PLOT_H - py(t.pct)} x2={plotW} className="stroke-line-divider" />
              ))}
              <rect width={plotW} height={PLOT_H} fill="none" className="stroke-line" rx={4} />

              {/* Trust bar + target price splits */}
              <line y1={barPx} y2={barPx} x2={plotW} className="stroke-line-strong" />
              <text x={plotW - 4} y={barPx - 5} textAnchor="end" className="fill-ink-muted stroke-surface font-mono text-[9.5px]" strokeWidth={3} style={HALO}>
                trust bar {TRUST_BAR}
              </text>
              {targetPx != null && (
                <>
                  <line x1={targetPx} x2={targetPx} y2={PLOT_H} className="stroke-line-strong" />
                  <text x={targetPx + 5} y={12} className="fill-ink-muted stroke-surface font-mono text-[9.5px]" strokeWidth={3} style={HALO}>
                    target {currencySymbol}
                    {targetPrice?.toLocaleString()}
                  </text>
                  <QuadrantLabel x={8} y={14} anchor="start" tone="fill-success-ink">BEST VALUE</QuadrantLabel>
                  <QuadrantLabel x={plotW - 8} y={14} anchor="end" tone="fill-action-deep">PREMIUM</QuadrantLabel>
                  <QuadrantLabel x={8} y={PLOT_H - 7} anchor="start" tone="fill-warn-ink">RISKY SAVINGS</QuadrantLabel>
                  <QuadrantLabel x={plotW - 8} y={PLOT_H - 7} anchor="end" tone="fill-danger-ink">AVOID</QuadrantLabel>
                </>
              )}

              {/* Names — only where they fit; the tooltip always carries them */}
              {pxPoints.map((p) => {
                const l = labels.get(p.name);
                if (!l) return null;
                return (
                  <text
                    key={`l${p.name}`}
                    x={l.x}
                    y={l.y}
                    textAnchor={l.anchor}
                    dominantBaseline="central"
                    strokeWidth={3}
                    style={HALO}
                    className={`stroke-surface text-[10.5px] font-semibold transition-opacity ${
                      p.shortlisted ? 'fill-ink' : 'fill-ink-secondary'
                    } ${hovered && hovered.name !== p.name ? 'opacity-35' : ''}`}
                  >
                    {p.name}
                  </text>
                );
              })}

              {/* Dots on top, with a surface ring so overlaps stay legible */}
              {pxPoints.map((p) => (
                <g key={p.name}>
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={5.5}
                    strokeWidth={p.shortlisted ? 2 : 1.5}
                    className={`transition-opacity ${
                      p.shortlisted ? 'fill-action stroke-surface' : 'fill-surface stroke-ink-muted'
                    } ${hovered && hovered.name !== p.name ? 'opacity-40' : ''}`}
                  />
                  {hovered?.name === p.name && (
                    <circle cx={p.x} cy={p.y} r={9} fill="none" strokeWidth={1.5} className={p.shortlisted ? 'stroke-action' : 'stroke-ink-muted'} />
                  )}
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={13}
                    fill="transparent"
                    onMouseEnter={() => setHovered(p)}
                    onMouseLeave={() => setHovered(null)}
                  />
                </g>
              ))}
            </g>

            {/* Axes */}
            <g transform={`translate(${MARGIN.left},${MARGIN.top})`} className="fill-ink-muted">
              {priceTicks.map((t) => (
                <text key={`tx${t.pct}`} x={px(t.pct)} y={PLOT_H + 14} textAnchor="middle" className="font-mono text-[9.5px] [font-variant-numeric:tabular-nums]">
                  {t.label}
                </text>
              ))}
              {trustTicks.map((t) => (
                <text key={`ty${t.pct}`} x={-8} y={PLOT_H - py(t.pct)} textAnchor="end" dominantBaseline="central" className="font-mono text-[9.5px] [font-variant-numeric:tabular-nums]">
                  {t.label}
                </text>
              ))}
              <text x={plotW / 2} y={PLOT_H + 30} textAnchor="middle" className="text-[10px] font-semibold uppercase tracking-[0.08em]">
                Unit price →
              </text>
              <text transform={`translate(${-MARGIN.left + 10},${PLOT_H / 2}) rotate(-90)`} textAnchor="middle" className="text-[10px] font-semibold uppercase tracking-[0.08em]">
                Trust score →
              </text>
            </g>
          </svg>
        )}

        {hovered && (
          <div
            className="pointer-events-none absolute z-10 rounded-control border border-line bg-surface px-s3 py-s2 shadow-floating"
            style={{
              left: tooltipLeft,
              top: MARGIN.top + hovered.y,
              transform: `translate(${tooltipFlip ? 'calc(-100% - 14px)' : '14px'}, -50%)`,
            }}
          >
            <div className="whitespace-nowrap text-[11px] font-semibold text-ink">
              {hovered.name}
              {hovered.shortlisted && <span className="ml-s2 font-normal text-action">shortlisted</span>}
            </div>
            <div className="whitespace-nowrap font-mono text-[10.5px] text-ink-secondary">
              {hovered.caption} · trust {hovered.trustScore}
            </div>
          </div>
        )}
      </div>

      {unplotted.length > 0 && (
        <p className="m-0 mt-s2 text-[10.5px] text-ink-muted">
          Not plotted (needs both a price and a trust score): {unplotted.join(', ')}
        </p>
      )}
    </div>
  );
};

export default SupplierTrustMatrix;
